import { ApplicationId } from "@1games-ru/region-shared/dist/enums/tablet/applicationd-id";
import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useState } from "react";
import clsx from "clsx";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import useGetRefreshableServerDateAndTime from "@/core/helpers/useGetRefreshableServerDateAndTime";
import { usePreloadImages } from "@/core/helpers/usePreloadImages";
import {
  imgHomeMenuTablet,
  imgTabletBackgroundLock,
  imgTabletBackgroundScreen,
} from "@/shared/assets/img";
import { KeyActionPanel } from "@/shared/ui";
import {
  devicesList,
  setActiveApp,
  tabletActiveApp,
  tabletIsVisible,
  tabltetIsShow,
} from "@/domains/tablet/slice";
import { factionsList } from "@/domains/faction/helpers/factionsList";
import { applicationsInfo } from "@/domains/tablet/helpers/applicationsInfo";
import useGetActionButtons from "@/domains/tablet/hooks/useGetActionButtons";
import useShowTablet from "@/domains/tablet/hooks/useShowTablet";
import BusinessTabletContainer from "@/domains/business/containers/tablet/BusinessTabletContainer";
import EstateTabletContainer from "@/domains/estate/containers/tablet/EstateTabletContainer.tsx";
import FactionContainer from "@/domains/faction/containers/FactionContainer";
import TransportWarhouseContainer from "@/domains/transportWarehouse/containers/TransportWarehouseContainer";
import TabletBody from "@/domains/tablet/components/TabletBody";
import TabletHomeScreen from "@/domains/tablet/components/TabletHomeScreen";
import TabletLockScreen from "@/domains/tablet/components/TabletLockScreen";
import TabletWrapper from "@/domains/tablet/components/TabletWrapper";

// Почему лучше снаружи
// 1. Стабильная ссылка. Если создавать объект в теле компонента, на каждом рендере это новый объект. Любые зависящие от него мемоизации/эффекты будут срабатывать лишний раз.
// 2. Нет лишней работы. Большая мапа с ссылками на компоненты будет пересоздаваться на каждый рендер без пользы.
// 3. Избежание ловушек замыканий. Внутри компонента легко «подхватить» локальные значения/пропсы в мапу и получить неожиданные зависимости.
// 4. Проще для TS/тришейкинга. На уровне модуля типы/экспорты прозрачно проверяются, сборщик может эффективнее трясти код.
// 5. Готово к lazy/code-splitting. Мапу удобнее превратить в React.lazy()-карты (динамический импорт) — это почти всегда делается на уровне модуля.
const appContent = {
  [ApplicationId.House]: EstateTabletContainer,
  [ApplicationId.Business]: BusinessTabletContainer,
  [ApplicationId.Faction]: FactionContainer,
  [ApplicationId.TransportWarehouse]: TransportWarhouseContainer,
  [ApplicationId.Marketplace]: null,
};

const imgList = [
  imgHomeMenuTablet,
  imgTabletBackgroundLock,
  imgTabletBackgroundScreen,
  ...Object.values(applicationsInfo).map((app) => app.icon),
  ...Object.values(factionsList).map((faction) => faction.src),
  ...Object.values(factionsList).map((faction) => faction.headerSrc),
  ...Object.values(factionsList).map((faction) => faction.experienceSrc),
];

export default function TabletContainer() {
  const dispatch = useAppDispatch();
  const activeApp = useAppSelector(tabletActiveApp);
  const [isLock, setIsLock] = useState(true);
  const [origin, setOrigin] = useState({ x: 0, y: 0 });
  const isOpen = useAppSelector(tabltetIsShow);
  const isVisible = useAppSelector(tabletIsVisible);
  const appsList = useAppSelector(devicesList);
  const currentTime = useGetRefreshableServerDateAndTime(isOpen, activeApp);

  const changeActiveApp = useCallback(
    (appId: ApplicationId) => dispatch(setActiveApp(appId)),
    [dispatch],
  );

  const tabletActionsButtons = useGetActionButtons();

  const handleIconClick = (event) => {
    const iconRect = event.currentTarget.getBoundingClientRect();
    const containerRect = event.currentTarget.parentElement.getBoundingClientRect();
    const x = iconRect.left - containerRect.left;
    const y = iconRect.top - containerRect.top;
    setOrigin({ x, y });
  };

  useShowTablet(setIsLock);

  const unlockTablet = useCallback(() => setIsLock(false), []);

  usePreloadImages(imgList);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={clsx(
            "fixed inset-y-[--boundary-offset-y] inset-x-[--boundary-offset-x] flex flex-col justify-center items-center z-40",
            { hidden: !isVisible },
          )}
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{
            y: "100%",
            transition: {
              delay: 0.2,
              stiffness: 400,
            },
          }}
          transition={{ stiffness: 400 }}
        >
          <TabletWrapper>
            <TabletBody>
              <motion.div
                className="absolute z-20 top-0 left-0 size-full pointer-events-none"
                exit={{ backgroundColor: "#121212" }}
              />
              <TabletLockScreen isLock={isLock} currentTime={currentTime} onClick={unlockTablet} />
              <AnimatePresence>
                {activeApp !== null && (
                  <motion.div
                    className="absolute z-1 top-0 left-0 size-full"
                    initial={{ scale: 0.1, opacity: 0, x: origin.x, y: origin.y }}
                    animate={{ scale: 1, opacity: 1, x: 0, y: 0 }}
                    exit={{ scale: 0.1, opacity: 0, x: origin.x, y: origin.y }}
                    transition={{ type: "spring", damping: 20, stiffness: 200 }}
                    style={{ originX: 0, originY: 0 }}
                  >
                    {React.createElement(appContent[activeApp])}
                  </motion.div>
                )}
              </AnimatePresence>
              <TabletHomeScreen
                appsList={appsList}
                currentTime={currentTime}
                changeActiveApp={changeActiveApp}
                handleIconClick={handleIconClick}
              />
            </TabletBody>
          </TabletWrapper>
          <KeyActionPanel
            name="tablet"
            actions={tabletActionsButtons}
            className="fixed bottom-[--boundary-offset-y] left-1/2 -translate-x-1/2 z-[51]"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
